<template>
  <div class="auth-page">
    <!-- Decorative background bubbles (pure CSS) -->
    <div class="auth-bg" aria-hidden="true">
      <span class="auth-blob auth-blob-1"></span>
      <span class="auth-blob auth-blob-2"></span>
      <span class="auth-blob auth-blob-3"></span>
      <span class="auth-bubble auth-bubble-1"></span>
      <span class="auth-bubble auth-bubble-2"></span>
      <span class="auth-bubble auth-bubble-3"></span>
      <span class="auth-bubble auth-bubble-4"></span>
      <span class="auth-bubble auth-bubble-5"></span>
    </div>

    <div class="auth-card">
      <!-- Brand / welcome panel -->
      <aside class="auth-hero">
        <span class="auth-eyebrow">Espace livreur</span>
        <h1 class="auth-hero-title">Bienvenue</h1>
        <p class="auth-hero-text">
          Retrouvez vos colis, vos tournées et vos paiements depuis un seul espace.
        </p>
        <div class="auth-logo-ring">
          <div class="auth-logo">
            <img src="@/assets/images/logo.png" alt="Logo" />
          </div>
        </div>
      </aside>

      <!-- Form panel -->
      <section class="auth-form-panel">
        <h2 class="auth-title">Se connecter</h2>
        <p class="auth-subtitle">Content de vous revoir ! Connectez-vous pour continuer.</p>

        <form @submit.prevent="handleLogin">
          <div class="mb-3">
            <label class="form-label" for="login-email">Adresse e-mail</label>
            <div class="auth-field">
              <i class="flaticon-user auth-field-icon" aria-hidden="true"></i>
              <input
                id="login-email"
                type="email"
                v-model="email"
                class="form-control auth-input"
                placeholder="vous@exemple.com"
                autocomplete="email"
              />
            </div>
          </div>

          <div class="mb-2">
            <label class="form-label" for="login-password">Mot de passe</label>
            <div class="auth-field">
              <i class="flaticon-lock auth-field-icon" aria-hidden="true"></i>
              <input
                id="login-password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                class="form-control auth-input auth-input-pass"
                placeholder="••••••••"
                autocomplete="current-password"
              />
              <button
                type="button"
                class="auth-eye"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
              >
                <i :class="showPassword ? 'flaticon-eye-1' : 'flaticon-eye'"></i>
                <span>{{ showPassword ? "Masquer" : "Afficher" }}</span>
              </button>
            </div>
          </div>

          <button type="submit" class="auth-submit">
            Se connecter
          </button>

          <p class="auth-footer-text">
            Vous n'avez pas de compte ?
            <router-link to="/register">Créer un compte</router-link>
          </p>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, getCurrentInstance } from "vue";
import axios from "axios";
import { useRouter } from "vue-router";
import api from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";
import { useSettingsStore } from "@/stores/settings";
import { useAppToast } from "@/composables/useAppToast";

export default {
  name: "LoginForm",

  setup() {
    // public settings: applies the brand colors (bubbles, buttons) before login
    const settingsStore = useSettingsStore();
    onMounted(() => settingsStore.fetchSettings());
    const { showToast } = useAppToast();

    const email = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const router = useRouter();
    const { proxy } = getCurrentInstance() || {};

    const handleLogin = async () => {
      try {
        const auth = useAuthStore();
        const response = await api.post("/api/auth/login", {
          email: email.value,
          password: password.value,
        });

        if (response.data && response.data.access_token && response.data.user) {
          // Assuming the API returns a token
          console.log("Login successful:", response.data.access_token);
          const token = response.data.access_token;
          auth.setToken(response.data.access_token); // Save token
          auth.setUser(response.data.user);
          auth.setPermissions(response.data.permissions);

          // console.log("User data:", auth.token, auth.user);

          // localStorage.setItem("permissions", response.permissions);
          // localStorage.setItem("notifs", response.count);

          // proxy.$showSuccessToast('Login successful');
          // toast.add({
          //   severity: "success",
          //   summary: "Success",
          //   detail: "Operation completed",
          //   life: 3000,
          // });
          showToast("Connexion réussie", "success");

          // console.log("Login successful:", response.data);
          // Redirect user after login
          router.push("/").then(() => {
            window.location.reload();
          });
        } else if(response.data =="User Archived"){
          showToast("Utilisateur archivé, contactez l'administrateur", "error");
          return;
        }else if(response.data =="Unauthorized"){
          showToast("Identifiants invalides", "error");
          return;
        }else if(response.data =="No perms"){
          showToast("Aucune permission attribuée, contactez l'administrateur", "error");
          return;
        }
        else {
          // Handle case where response does not contain expected data
          console.error("Login failed: Invalid response structure");
          // proxy.$showErrorToast('Login failed');
          // toast.add({
          //   severity: "error",
          //   summary: "Error",
          //   detail: "Invalid response structure",
          //   life: 3000,
          // });
          alert("Login failed: Invalid response structure");
          return;
        }
      } catch (error) {
        console.error("Login failed:", error.response?.data || error.message);
        // alert(error.response?.data?.message || "Login failed");
      }
    };

    return {
      showPassword,
      email,
      password,
      handleLogin,
    };
  },
};
</script>

<style scoped>
/* Values come from the design tokens (assets/css/theme/_tokens.scss). */
.auth-page {
  position: relative;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: var(--ui-space-6) var(--ui-space-4);
  overflow: hidden;
  background: var(--ui-bg);
}

/* ── Background bubbles ──────────────────────────────────────────── */
.auth-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.auth-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  background: radial-gradient(circle, rgba(var(--ui-accent-rgb), 0.16), rgba(var(--ui-accent-rgb), 0) 70%);
  animation: auth-drift 26s ease-in-out infinite;
}
.auth-blob-1 { width: 520px; height: 520px; top: -160px; left: -140px; }
.auth-blob-2 { width: 460px; height: 460px; bottom: -180px; right: -120px; animation-delay: -8s; }
.auth-blob-3 { width: 320px; height: 320px; top: 40%; left: 55%; animation-delay: -15s; opacity: 0.7; }

.auth-bubble {
  position: absolute;
  bottom: -8rem;
  border-radius: 50%;
  background: radial-gradient(circle at 32% 28%, rgba(255, 255, 255, 0.9), color-mix(in srgb, var(--ui-accent-light) 45%, transparent) 45%, rgba(var(--ui-accent-rgb), 0.32) 100%);
  border: 1px solid rgba(255, 255, 255, 0.65);
  box-shadow: inset 0 -6px 14px rgba(var(--ui-accent-rgb), 0.2), 0 8px 26px rgba(var(--ui-accent-rgb), 0.16);
  animation: auth-rise 18s linear infinite;
}
.auth-bubble-1 { width: 70px;  height: 70px;  left: 8%;  animation-duration: 19s; }
.auth-bubble-2 { width: 130px; height: 130px; left: 22%; animation-duration: 24s; animation-delay: -9s; }
.auth-bubble-3 { width: 56px;  height: 56px;  left: 70%; animation-duration: 16s; animation-delay: -4s; }
.auth-bubble-4 { width: 110px; height: 110px; left: 84%; animation-duration: 22s; animation-delay: -13s; }
.auth-bubble-5 { width: 40px;  height: 40px;  left: 48%; animation-duration: 14s; animation-delay: -7s; }

@keyframes auth-drift {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  33% { transform: translate3d(6vw, -5vh, 0) scale(1.12); }
  66% { transform: translate3d(-4vw, 4vh, 0) scale(0.92); }
}
@keyframes auth-rise {
  0%   { transform: translate3d(0, 8vh, 0) scale(0.7); opacity: 0; }
  10%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translate3d(4vw, -110vh, 0) scale(1.2); opacity: 0; }
}

/* ── Card ────────────────────────────────────────────────────────── */
.auth-card {
  position: relative;
  z-index: 1;
  width: min(1120px, 100%);
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: var(--ui-radius-2xl);
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  box-shadow: 0 30px 80px rgba(var(--ui-accent-rgb), 0.14), 0 4px 16px rgba(28, 25, 23, 0.04);
  overflow: hidden;
}

/* Brand panel */
.auth-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 56px 56px 48px;
  color: #FFFFFF;
  background:
    radial-gradient(circle at 15% 105%, rgba(0, 0, 0, 0.12) 0 38%, transparent 38.5%),
    radial-gradient(circle at 110% -10%, rgba(255, 255, 255, 0.14) 0 30%, transparent 30.5%),
    linear-gradient(145deg, var(--ui-accent-light) 0%, var(--ui-accent) 45%, var(--ui-accent-strong) 100%);
}
.auth-eyebrow {
  display: inline-block;
  padding: 6px 16px;
  border-radius: var(--ui-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.16);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-black);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.auth-hero-title {
  margin: 18px 0 10px;
  color: #FFFFFF;
  font-size: clamp(2.5rem, 5vw, 3.75rem);
  font-weight: var(--ui-fw-black);
  line-height: 1;
  text-transform: uppercase;
}
.auth-hero-text {
  max-width: 26rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-semibold);
  line-height: 1.6;
}
.auth-logo-ring {
  align-self: center;
  margin-top: auto;
  padding: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.14);
}
.auth-logo {
  display: grid;
  place-items: center;
  width: 190px;
  height: 190px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: inset 0 -6px 16px rgba(var(--ui-accent-rgb), 0.12);
}
.auth-logo img {
  width: 80%;
  height: auto;
}

/* Form panel */
.auth-form-panel {
  padding: 64px 60px 56px;
}
.auth-title {
  margin-bottom: 8px;
  color: var(--ui-text);
  font-size: clamp(1.9rem, 3vw, 2.5rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
}
.auth-subtitle {
  margin-bottom: 28px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-md);
  line-height: 1.55;
}

.auth-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 28px;
  padding: 5px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  border: 1px solid var(--ui-border);
}
.auth-tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-base);
  font-weight: var(--ui-fw-heavy);
  transition: background-color var(--ui-dur) var(--ui-ease), color var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease);
}
.auth-tab i { line-height: 1; font-size: 14px; }
.auth-tab:not(.active):hover {
  color: var(--ui-accent-text);
  background: var(--ui-accent-soft);
}
.auth-tab.active {
  color: #FFFFFF;
  background: var(--ui-accent);
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.28);
}

.auth-field {
  position: relative;
}
.auth-field-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  z-index: 1;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  font-size: 16px;
  line-height: 1;
  pointer-events: none;
  transition: color var(--ui-dur) var(--ui-ease);
}
.auth-field:focus-within .auth-field-icon {
  color: var(--ui-accent);
}
.auth-input {
  min-height: 56px;
  padding-left: 48px;
  font-size: var(--ui-fs-md);
}
.auth-input-pass {
  padding-right: 124px;
}
.auth-eye {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: 0;
  background: none;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
}
.auth-eye:hover { color: var(--ui-accent-text); }

.auth-link {
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  text-decoration: none;
}
.auth-link:hover { color: var(--ui-accent-strong); }

.auth-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 58px;
  margin-top: 28px;
  border: 0;
  border-radius: var(--ui-radius-md);
  background: linear-gradient(180deg, var(--ui-accent-light) 0%, var(--ui-accent) 60%, var(--ui-accent-strong) 100%);
  color: #FFFFFF;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-heavy);
  box-shadow: var(--ui-shadow-accent);
  transition: transform var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease), opacity var(--ui-dur-fast) var(--ui-ease);
}
.auth-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(var(--ui-accent-rgb), 0.4);
}
.auth-submit:active:not(:disabled) { transform: translateY(0); }
.auth-submit:disabled { opacity: 0.6; cursor: not-allowed; }

.auth-footer-text {
  margin: 18px 0 0;
  text-align: center;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.auth-footer-text a {
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-heavy);
  text-decoration: none;
}

/* ── Responsive ──────────────────────────────────────────────────── */
@media (max-width: 991.98px) {
  .auth-card { grid-template-columns: 1fr; max-width: 560px; }
  .auth-hero {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    justify-items: start;
    gap: 6px 20px;
    padding: 28px 32px;
  }
  .auth-hero > :not(.auth-logo-ring) { grid-column: 2; }
  .auth-hero-title { margin: 4px 0 0; font-size: 2.25rem; }
  .auth-hero-text { font-size: var(--ui-fs-base); }
  .auth-logo-ring { grid-column: 1; grid-row: 1 / span 3; align-self: center; margin: 0; padding: 8px; }
  .auth-logo { width: 84px; height: 84px; }
  .auth-form-panel { padding: 36px 32px 40px; }
}
@media (max-width: 575.98px) {
  .auth-page { align-items: start; padding: var(--ui-space-4); }
  .auth-card { border-radius: var(--ui-radius-xl); }
  .auth-hero { padding: 20px; gap: 6px 14px; }
  .auth-hero-title { font-size: 1.9rem; }
  .auth-hero-text { display: none; }
  .auth-logo { width: 72px; height: 72px; }
  .auth-eyebrow { font-size: 10px; padding: 5px 12px; }
  .auth-form-panel { padding: 28px 20px 32px; }
  .auth-input { min-height: 52px; }
  .auth-eye span { display: none; }
  .auth-input-pass { padding-right: 52px; }
}

/* ── Dark mode (body.dark) ───────────────────────────────────────── */
:global(.dark) .auth-card { box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55); }
:global(.dark) .auth-bubble { opacity: 0.35; }

@media (prefers-reduced-motion: reduce) {
  .auth-blob { animation: none; }
  .auth-bubble { animation: none; opacity: 0; }
}
</style>
